package one.healthium.ricediet.presentation.feature.today

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Icon
import androidx.compose.material3.LocalTextStyle
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import ifaandroid.apps.ricediet.composeapp.generated.resources.ic_plus_34
import ifaandroid.core.ui_kit.generated.resources.Res
import ifaandroid.core.ui_kit.generated.resources.ic_bottle
import ifaandroid.core.ui_kit.generated.resources.ic_file_checked
import ifaandroid.core.ui_kit.generated.resources.ic_pizza
import ifaandroid.core.ui_kit.generated.resources.ic_steps
import loco.GENERAL_OPEN
import loco.GENERAL_STEPS
import loco.TODAY_LOGGED_SYMPTOMS_LABEL
import loco.TODAY_LOG_CONTROLS_SECTION
import loco.TODAY_LOG_SYMPTOMS_LABEL
import loco.TODAY_LOG_WATER_BUTTON
import loco.TODAY_LOG_WEIGHT_BUTTON
import loco.TODAY_MEAL_PLAN_SECTION
import one.healthium.base.utils.Formatters.formatWeight
import one.healthium.base.utils.relativeDate
import one.healthium.base.utils.toVolumeString
import one.healthium.base.utils.toVolumeUnit
import one.healthium.healthKit.ui.HealthKitConnectBanner
import one.healthium.healthKit.ui.HealthKitConnectBannerContract
import one.healthium.loco.resolve
import one.healthium.meal_plans.domain.MealPlanInfoModel
import one.healthium.rateus.data.ShowAppRateForStart
import one.healthium.ricediet.rice
import one.healthium.today.ui.TodayUiState
import one.healthium.today.ui.TodayViewModel
import one.healthium.today.ui.models.DailyStepsUiModel
import one.healthium.ui.common.message.AlertUiMessageHandler
import one.healthium.ui.compose.components.AppButton
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.platform.PlatformColorsEffect
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.black30
import one.healthium.ui.compose.theme.white10
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import androidx.compose.ui.tooling.preview.Preview
import org.koin.compose.viewmodel.koinViewModel

interface TodayEventDelegate {
    fun onMealPlanClicked(id: String)
    fun onLogFoodClicked()
    fun onLogWaterClicked()
    fun onLogWeightClicked()
    fun onLogSymptomsClicked()
}

@Composable
fun TodayScreen(
    eventDelegate: TodayEventDelegate
) {
    PlatformColorsEffect(lightStatusBar = true)

    val viewModel = koinViewModel<TodayViewModel>()
    val state by viewModel.state().collectAsState()

    ShowAppRateForStart()

    TodayScreenContent(
        state = state,
        onLogWaterClicked = eventDelegate::onLogWaterClicked,
        onLogWeightClicked = eventDelegate::onLogWeightClicked,
        onMealPlanClicked = { eventDelegate.onMealPlanClicked(it.id) },
        onAddFoodClicked = eventDelegate::onLogFoodClicked,
        onLogSymptomsClicked = eventDelegate::onLogSymptomsClicked,
        healthKitContract = viewModel,
    )

    AlertUiMessageHandler(viewModel.uiMessages)
}

@Composable
internal fun TodayScreenContent(
    state: TodayUiState,
    onLogWaterClicked: () -> Unit,
    onLogWeightClicked: () -> Unit,
    onMealPlanClicked: (MealPlanInfoModel) -> Unit,
    onAddFoodClicked: () -> Unit,
    onLogSymptomsClicked: () -> Unit,
    healthKitContract: HealthKitConnectBannerContract,
) {
    AppScaffold(Modifier.testTag("today_screen")) {
        Column(
            modifier = Modifier.fillMaxSize().verticalScroll(rememberScrollState()),
        ) {
            CaloriesCounter(
                state = state,
                onAddFoodClicked = onAddFoodClicked,
                onLogSymptomsClicked = onLogSymptomsClicked,
            )
            if (state.shouldShowHKBanner) {
                Spacer(Modifier.height(12.dp))
                HealthKitConnectBanner(contract = healthKitContract)
                Spacer(Modifier.height(12.dp))
            }
            MealPlanSection(state, onMealPlanClicked)
            DailyControlsSection(state, onLogWeightClicked, onLogWaterClicked, onLogSymptomsClicked)
            Spacer(Modifier.height(80.dp))
        }
    }
}

@Composable
private fun MealPlanSection(
    state: TodayUiState,
    onMealPlanClicked: (MealPlanInfoModel) -> Unit
) {
    val mealPlan = state.mealPlan
    if (mealPlan != null) {
        Section(stringResource(loco.Res.string.TODAY_MEAL_PLAN_SECTION))
        MealPlanItem(
            modifier = Modifier.padding(horizontal = 12.dp),
            mealPlan = mealPlan,
            onMealPlanClicked = onMealPlanClicked,
        )
    }
}

@Composable
private fun DailyControlsSection(
    state: TodayUiState,
    onLogWeightClicked: () -> Unit,
    onLogWaterClicked: () -> Unit,
    onLogSymptomsClicked: () -> Unit,
) {
    Section(stringResource(loco.Res.string.TODAY_LOG_CONTROLS_SECTION))
    LogWeightItem(
        state = state,
        modifier = Modifier
            .padding(horizontal = 12.dp)
            .padding(bottom = 12.dp),
        onLogWeightClicked = onLogWeightClicked,
    )

    LogWaterItem(
        state = state,
        modifier = Modifier
            .padding(horizontal = 12.dp)
            .padding(bottom = 12.dp),
        onWaterLogClicked = onLogWaterClicked,
    )

    LogSymptomsItem(
        state = state,
        modifier = Modifier
            .padding(horizontal = 12.dp)
            .padding(bottom = 12.dp),
        onClick = onLogSymptomsClicked,
    )
    state.todayStepsCounter?.let {
        StepsCounterItem(
            modifier = Modifier
                .padding(horizontal = 12.dp)
                .padding(bottom = 12.dp),
            steps = it,
        )
    }
}

@Composable
private fun Section(text: String) {
    Text(
        modifier = Modifier.padding(horizontal = 16.dp).padding(top = 24.dp, bottom = 12.dp),
        text = text,
        style = AppTheme.textStyles.secondary_17_500,
        color = AppTheme.colors.text400,
    )
}

@Composable
private fun MealPlanItem(
    modifier: Modifier = Modifier,
    mealPlan: MealPlanInfoModel,
    onMealPlanClicked: (MealPlanInfoModel) -> Unit,
) {
    BaseLogControl(
        modifier = modifier,
        icon = Res.drawable.ic_pizza,
        button = stringResource(loco.Res.string.GENERAL_OPEN),
        onClick = { onMealPlanClicked(mealPlan) },
        content = {
            Text(
                modifier = Modifier.weight(1f),
                text = mealPlan.name.resolve(),
                style = AppTheme.textStyles.secondary_17_500,
                color = AppTheme.colors.text100,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
            )
        }
    )
}

@Composable
private fun LogWeightItem(
    state: TodayUiState,
    modifier: Modifier = Modifier,
    onLogWeightClicked: () -> Unit
) {
    var weightValue: String? = null
    var dateValue: String? = null

    val latestWeightLog = state.latestWeightLog
    if (latestWeightLog != null) {
        weightValue = latestWeightLog.currentWeight.formatWeight(state.isMetric).resolve()
        dateValue = latestWeightLog.date?.relativeDate()?.resolve().orEmpty()
    }
    BaseLogControl(
        modifier = modifier,
        icon = Res.drawable.ic_file_checked,
        button = stringResource(loco.Res.string.TODAY_LOG_WEIGHT_BUTTON),
        onClick = onLogWeightClicked,
    ) {
        Column(
            modifier = Modifier.weight(1f),
        ) {
            if (!weightValue.isNullOrBlank()) {
                Text(
                    text = weightValue,
                    style = AppTheme.textStyles.secondary_17_500,
                    color = AppTheme.colors.text100,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }
            if (!dateValue.isNullOrBlank()) {
                Text(
                    text = dateValue,
                    style = AppTheme.textStyles.secondary_17_400,
                    color = black30,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }
        }
    }
}

@Composable
private fun LogWaterItem(
    state: TodayUiState,
    modifier: Modifier = Modifier,
    onWaterLogClicked: () -> Unit
) {
    BaseLogControl(
        modifier = modifier,
        icon = Res.drawable.ic_bottle,
        button = stringResource(loco.Res.string.TODAY_LOG_WATER_BUTTON),
        onClick = onWaterLogClicked,
        content = {
            SingleLineLogContent(
                contentPrimary = "${state.todayWaterIntake?.currentValue?.toVolumeString(state.isMetric)}" +
                        " / " +
                        "${state.todayWaterIntake?.dailyAmount?.toVolumeString(state.isMetric)}",
                contentSecondary = " ${state.isMetric.toVolumeUnit().resolve()}"
            )
        },
    )
}

@Composable
private fun LogSymptomsItem(
    state: TodayUiState,
    modifier: Modifier = Modifier,
    onClick: () -> Unit
) {
    BaseLogControl(
        modifier = modifier,
        icon = ifaandroid.apps.ricediet.composeapp.generated.resources.Res.drawable.ic_plus_34,
        button = stringResource(loco.Res.string.TODAY_LOG_SYMPTOMS_LABEL),
        onClick = onClick,
        content = {
            SingleLineLogContent(
                contentPrimary = state.todayLoggedSymptoms.toString(),
                contentSecondary = " ${stringResource(loco.Res.string.TODAY_LOGGED_SYMPTOMS_LABEL)}"
            )
        },
    )
}

@Composable
private fun StepsCounterItem(
    modifier: Modifier = Modifier,
    steps: DailyStepsUiModel,
) {
    BaseLogControl(
        modifier = modifier,
        icon = Res.drawable.ic_steps,
        content = {
            SingleLineLogContent(
                contentPrimary = "${steps.steps} / ${steps.goal}",
                contentSecondary = " ${stringResource(loco.Res.string.GENERAL_STEPS)}"
            )
        },
    )
}

@Composable
private fun RowScope.SingleLineLogContent(
    contentPrimary: String,
    contentSecondary: String,
) {
    Text(
        text = contentPrimary,
        style = AppTheme.textStyles.secondary_17_500,
        color = AppTheme.colors.text100,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
    )
    Text(
        text = contentSecondary,
        style = AppTheme.textStyles.secondary_17_400,
        color = black30,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
    )
    Spacer(Modifier.weight(1f))
}

@Composable
internal fun BaseLogControl(
    modifier: Modifier = Modifier.Companion,
    icon: DrawableResource,
    button: String? = null,
    onClick: () -> Unit = {},
    content: @Composable RowScope.() -> Unit,
) {
    Surface(
        modifier = modifier.fillMaxWidth(),
        shape = AppTheme.shapes.large,
        onClick = onClick,
        shadowElevation = 1.dp,
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 14.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Spacer(Modifier.width(5.dp))
            Box(
                modifier = Modifier
                    .padding(vertical = 2.dp)
                    .size(36.dp)
                    .background(white10, CircleShape),
                contentAlignment = Alignment.Center,
            ) {
                Icon(
                    modifier = Modifier.size(34.dp),
                    painter = painterResource(icon),
                    tint = AppTheme.colors.primary.copy(alpha = 0.5f),
                    contentDescription = null,
                )
            }
            Spacer(Modifier.width(12.dp))
            content()
            Spacer(Modifier.width(8.dp))
            if (button != null) {
                LogControlButton(button, onClick)
            }
        }
    }
}

@Composable
private fun LogControlButton(
    title: String,
    onClick: () -> Unit,
) {
    AppButton(
        modifier = Modifier.height(40.dp),
        contentPadding = PaddingValues(horizontal = 12.dp),
        onClick = onClick,
    ) {
        Text(text = title, style = LocalTextStyle.current.copy(fontWeight = FontWeight.SemiBold))
    }
}

@Composable
@Preview
private fun TodayScreenWidthPreview() {
    AppTheme(theme = rice()) {
        TodayScreenContent(
            state = TodayUiState.preview,
            onLogWaterClicked = {},
            onLogWeightClicked = {},
            onMealPlanClicked = {},
            onAddFoodClicked = {},
            onLogSymptomsClicked = {},
            healthKitContract = object : HealthKitConnectBannerContract {
                override fun onSkipClicked() = Unit
            }
        )
    }
}
